import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const PLAN_LIMITS = { pack_one: 2, pack_two: 5, pack_advanced: 20 };

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

export default function Agents() {
  const { user } = useAuth();
  const maxAgents = PLAN_LIMITS[user?.plan_key] || 2;
  const [agents, setAgents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const load = () => axios.get('/api/agents').then(r => setAgents(r.data.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const addAgent = async () => {
    if (!form.name || !form.email || !form.password) return toast.error('Fill all fields');
    setLoading(true);
    try {
      await axios.post('/api/agents', { ...form, role: 'agent' });
      toast.success('Agent added!');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const saveEdit = async () => {
    if (!editAgent.name || !editAgent.email) return toast.error('Fill all fields');
    setLoading(true);
    try {
      await axios.put(`/api/agents/${editAgent.id}`, { name: editAgent.name, email: editAgent.email, role: editAgent.role, is_active: editAgent.is_active });
      toast.success('Agent updated!');
      setEditAgent(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setLoading(false); }
  };

  const deleteAgent = async (agent) => {
    if (!window.confirm(`Delete agent ${agent.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/agents/${agent.id}`);
      toast.success('Agent deleted');
      load();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const blockAgent = async (agent) => {
    const newStatus = agent.is_active ? 0 : 1;
    await axios.put(`/api/agents/${agent.id}`, { name: agent.name, email: agent.email, role: agent.role, is_active: newStatus });
    toast.success(newStatus ? 'Agent unblocked' : 'Agent blocked');
    load();
  };

  const resetPassword = async () => {
    if (!newPass || newPass.length < 8) return toast.error('Min 8 characters');
    await axios.put(`/api/agents/${resetModal.id}/reset-password`, { new_password: newPass });
    toast.success('Password reset!');
    setResetModal(null);
    setNewPass('');
  };

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>Agents</h1>
            <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>{agents.length} agents in your team</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
              <span style={{ color: agents.length >= maxAgents ? '#ff4444' : '#00d55b', fontWeight: 700 }}>{agents.length}</span>
              <span style={{ color: '#555' }}> / {maxAgents} agents</span>
            </div>
            {agents.length < maxAgents ? (
              <button onClick={() => setShowAdd(true)}
                style={{ padding: '10px 20px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}>
                + Add Agent
              </button>
            ) : (
              <div style={{ padding: '10px 16px', background: 'rgba(255,68,68,0.1)', border: '1px solid #ff4444', borderRadius: 8, fontSize: 13, color: '#ff4444' }}>
                Plan limit reached
              </div>
            )}
          </div>
        </div>

        {/* Add Modal */}
        {showAdd && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Add New Agent</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Full Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Agent Name" /></div>
                <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="agent@company.com" /></div>
                <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Password *</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" /></div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={addAgent} disabled={loading} style={{ flex: 2, padding: '10px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Adding...' : 'Add Agent'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editAgent && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Edit Agent</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Full Name *</label>
                  <input value={editAgent.name} onChange={e => setEditAgent(a => ({ ...a, name: e.target.value }))} /></div>
                <div><label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Email *</label>
                  <input type="email" value={editAgent.email} onChange={e => setEditAgent(a => ({ ...a, email: e.target.value }))} /></div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button onClick={() => setEditAgent(null)} style={{ flex: 1, padding: '10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={saveEdit} disabled={loading} style={{ flex: 2, padding: '10px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Reset Password</h3>
              <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>New password for <strong style={{ color: '#fff' }}>{resetModal.name}</strong></p>
              <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Min 8 characters" style={{ marginBottom: 16 }} />
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setResetModal(null); setNewPass(''); }} style={{ flex: 1, padding: '10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button onClick={resetPassword} style={{ flex: 2, padding: '10px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Reset</button>
              </div>
            </div>
          </div>
        )}

        {/* Agents list */}
        {agents.length === 0 ? (
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 40, textAlign: 'center', color: '#333' }}>
            No agents yet. Add your first agent!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {agents.map(agent => (
              <div key={agent.id} style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, opacity: agent.is_active ? 1 : 0.6 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#00d55b', flexShrink: 0 }}>
                  {agent.name?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: agent.is_active ? '#fff' : '#555' }}>{agent.name}</div>
                  <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{agent.email}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: agent.online_status === 'online' ? '#00d55b' : '#333' }}></div>
                  <span style={{ fontSize: 12, color: agent.online_status === 'online' ? '#00d55b' : '#555' }}>{agent.online_status}</span>
                </div>
                {!agent.is_active && <span style={{ fontSize: 11, color: '#ff4444', background: 'rgba(255,68,68,0.1)', padding: '2px 8px', borderRadius: 20 }}>Blocked</span>}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditAgent({ ...agent })} style={{ padding: '6px 12px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✏️ Edit</button>
                  <button onClick={() => blockAgent(agent)} style={{ padding: '6px 12px', background: agent.is_active ? 'rgba(255,165,0,0.1)' : 'rgba(0,213,91,0.1)', border: `1px solid ${agent.is_active ? '#f97316' : '#00d55b'}`, color: agent.is_active ? '#f97316' : '#00d55b', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                    {agent.is_active ? '🚫 Block' : '✅ Unblock'}
                  </button>
                  <button onClick={() => setResetModal(agent)} style={{ padding: '6px 12px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🔑 Reset</button>
                  <button onClick={() => deleteAgent(agent)} style={{ padding: '6px 12px', background: 'rgba(255,68,68,0.1)', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🗑️ Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashLayout>
  );
}